import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
import { UseDateFieldProps } from '@mui/x-date-pickers/DateField';
import {
  DatePickerProps,
  DatePicker as MuiDatePicker,
} from '@mui/x-date-pickers/DatePicker';
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
} from '@mui/x-date-pickers/models';
import { useState } from 'react';

import { Icon, type IconDefinition, type IconProps } from './Icon';
import { IconButton, type IconButtonProps } from './IconButton';

interface ButtonFieldProps
  extends UseDateFieldProps<Date, false>,
    BaseSingleInputFieldProps<
      Date | null,
      Date,
      FieldSection,
      false,
      DateValidationError
    > {
  buttonSize?: IconButtonProps['size'];
  className?: IconButtonProps['className'];
  icon?: IconDefinition;
  iconSize?: IconProps['size'];
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  sx?: IconButtonProps['sx'];
}

function ButtonField(props: ButtonFieldProps) {
  const {
    buttonSize,
    className,
    icon = faCalendarDay,
    iconSize,
    setOpen,
    sx,
    id,
    disabled,
    InputProps: { ref } = {},
    inputProps: { 'aria-label': ariaLabel } = {},
  } = props;

  return (
    <IconButton
      id={id}
      className={className}
      disabled={disabled}
      ref={ref}
      aria-label={ariaLabel}
      onClick={() => setOpen?.((prev) => !prev)}
      size={buttonSize}
      sx={sx}
    >
      <Icon icon={icon} size={iconSize} />
    </IconButton>
  );
}

interface IconDatePickerProps
  extends Omit<
    DatePickerProps<Date>,
    'open' | 'onOpen' | 'onClose' | 'slots'
  > {
  buttonSize?: IconButtonProps['size'];
  className?: IconButtonProps['className'];
  icon?: IconDefinition;
  iconSize?: IconProps['size'];
  slots?: Omit<NonNullable<DatePickerProps<Date>['slots']>, 'field'>;
  sx?: IconButtonProps['sx'];
}

export const IconDatePicker: React.FC<IconDatePickerProps> = ({
  buttonSize,
  className,
  icon,
  iconSize,
  sx,
  slots,
  slotProps,
  ...props
}) => {
  const [open, setOpen] = useState(false);

  return (
    <MuiDatePicker
      slots={{ ...slots, field: ButtonField }}
      slotProps={{
        ...slotProps,
        field: (ownerState) => ({
          ...(typeof slotProps?.field === 'function'
            ? slotProps.field(ownerState)
            : slotProps?.field),
          buttonSize,
          className,
          icon,
          iconSize,
          setOpen,
          sx,
        }),
      }}
      {...props}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    />
  );
};
